import { Button, Dialog, DialogActions, DialogContent, Divider } from '@mui/material';
import { get, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { FormProvider, Scrollbar } from '@app/components';
import { IAddCandidateSchema } from '../types';
import { addCandidateSchema } from '../validators';
import { AddCandidateForm } from './add-candidate-form';
import { addCandidate, updateCandidate } from '../slice';
import { selectAddCandidateState, selectUpdateCandidateState } from '../selectors';
import { useRequestState } from '@app/hooks';

// ------------------------------------------------
interface props {
  open: boolean;
  onClose: VoidFunction;
  row?: IAddCandidateSchema;
}

const AddCandidatePage = ({ open, onClose, row }: props) => {
  const parts = row?.resume.split('?');
  const filePickerName = parts ? parts[0].split('/').pop() : '';
  const dispatch = useDispatch();
  const methods = useForm<IAddCandidateSchema>({
    resolver: yupResolver(addCandidateSchema) as any,
    defaultValues: {
      firstName: get(row, 'firstName', ''),
      lastName: get(row, 'lastName', ''),
      phone: get(row, 'phone', ''),
      personalEmail: get(row, 'email', ''),
      source: get(row, 'source', ''),
      referenceBy: get(row, 'referenceBy', ''),
      other: get(row, 'other', ''),
      resume: get(row, 'resume', ''),
      applyingFor: get(row, 'position', ''),
    },
  });
  const { loading } = useRequestState({
    stateSelector: row ? selectUpdateCandidateState : selectAddCandidateState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });
  const { control } = methods;
  const resume = useWatch({ control, name: 'resume' });

  const onSubmit = (form: IAddCandidateSchema) => {
    row
      ? dispatch(updateCandidate({ ...form, id: row?.id, roleId: row?.roleId, resume }))
      : dispatch(addCandidate({ ...form, roleId: 4, resume }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Header
        title={row ? Strings.addCandidate.updateCandidate : Strings.addCandidate.manageCandidate}
        variant="dialog"
        onClose={onClose}
      />
      <Scrollbar>
        <DialogContent>
          <FormProvider methods={methods}>
            <AddCandidateForm filePickerName={filePickerName} />
          </FormProvider>
        </DialogContent>
      </Scrollbar>
      <Divider />
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose}>
          {Strings.button.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={loading}
          onClick={methods.handleSubmit(onSubmit)}
        >
          {Strings.button.saveCandidate}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export { AddCandidatePage };
